/**
 * The unfinished-signup reminder's one-click finish link (api-service#1011) lands on `/login#finish=<token>`.
 * This module is deliberately independent of React.
 *
 * The token rides in the FRAGMENT on purpose: browsers never send it to the server that serves the
 * page, so it reaches no access log, and the mail scanners that prefetch every URL in a message spend
 * nothing by fetching the page. The login page lifts it out here and posts it to the api.
 */

const FINISH_KEY = "finish";
/** The reminder's stop link (api-service#1019): `/login#stop=<token>`, redeemed by deleting the stub. */
const STOP_KEY = "stop";

function fragmentValue(hash: string, key: string): string | null {
	const params = new URLSearchParams(hash.replace(/^#/, ""));
	const token = params.get(key);
	return token !== null && token.length > 0 ? token : null;
}

/** Parses the finish token out of a `location.hash`-style string; null when none is present. */
export function parseFinishFragment(hash: string): string | null {
	return fragmentValue(hash, FINISH_KEY);
}

/** Parses the stop token out of a `location.hash`-style string; null when none is present. */
export function parseStopFragment(hash: string): string | null {
	return fragmentValue(hash, STOP_KEY);
}

interface CapturedTokens {
	finish: string | null;
	stop: string | null;
}

const NONE: CapturedTokens = {finish: null, stop: null};
let captured: CapturedTokens | null = null;

/**
 * Reads BOTH reminder tokens from one snapshot of the fragment, strips the fragment from the address
 * bar on the first success (so a reload or a copied URL never carries a bearer token onward), and
 * caches the pair for every later call this page load. Answers nothing during SSG pre-render.
 *
 * Both are read TOGETHER on purpose. Read independently, the first read's scrub emptied the hash
 * before the second one looked at it, so a fragment carrying both tokens silently lost whichever was
 * asked for second — which is exactly the fragment the confirm card's stop control needs (ui#1688).
 */
function captureLinkTokens(): CapturedTokens {
	if (captured !== null) {
		return captured;
	}
	if (typeof window === "undefined") {
		return NONE;
	}
	const hash = window.location.hash;
	const tokens: CapturedTokens = {finish: parseFinishFragment(hash), stop: parseStopFragment(hash)};
	if (tokens.finish === null && tokens.stop === null) {
		// Nothing of ours in the fragment: leave it exactly as it is (a download page's share key lives
		// there too) and stay uncached, so this costs a caller nothing and decides nothing.
		return NONE;
	}
	history.replaceState(history.state, "", window.location.pathname + window.location.search);
	captured = tokens;
	return captured;
}

/** The finish token from this page load's fragment, or null. @see captureLinkTokens */
export function captureFinishLinkToken(): string | null {
	return captureLinkTokens().finish;
}

/** The stop token from this page load's fragment, or null. @see captureLinkTokens */
export function captureStopLinkToken(): string | null {
	return captureLinkTokens().stop;
}

/** Clears the per-page-load capture cache. Exists for tests ONLY — never call it in app code. */
export function __resetFinishLinkCaptureForTests(): void {
	captured = null;
}
